import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Divider, Chip, Alert, Grid,
  Table, TableRow, TableCell, TableBody, TextField, MenuItem, Link,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { installationsApi } from '../../../api/installations.api';
import { FileUploadField } from '../../../components/FileUploadField';
import { extractApiError } from '../../../api/error';
import { StatusBadge } from '../../../components/StatusBadge';
import { useAuth } from '../../../hooks/useAuth';
import { PdiCommentsThread } from '../components/PdiCommentsThread';

interface Props {
  installationId: number | null;
  onClose: () => void;
  onChanged: () => void;
}

export function InstallationDetailDialog({ installationId, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = installationId !== null;
  const id = installationId ?? 0;
  const [error, setError] = useState<string | null>(null);
  const [technicianId, setTechnicianId] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [certUrl, setCertUrl] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [chkTitle, setChkTitle] = useState('');
  const [photoKind, setPhotoKind] = useState('before');
  const [photoUrl, setPhotoUrl] = useState('');

  const { data: r } = useQuery({ queryKey: ['installation', id], queryFn: () => installationsApi.get(id), enabled: open });
  const refresh = () => { setError(null); qc.invalidateQueries({ queryKey: ['installation', id] }); onChanged(); };
  const onErr = (e: unknown) => setError(extractApiError(e));

  const assignMut = useMutation({ mutationFn: () => installationsApi.assign(id, { technicianId: Number(technicianId) }), onSuccess: refresh, onError: onErr });
  const scheduleMut = useMutation({ mutationFn: () => installationsApi.schedule(id, { scheduledDate }), onSuccess: refresh, onError: onErr });
  const startMut = useMutation({ mutationFn: () => installationsApi.start(id), onSuccess: refresh, onError: onErr });
  const materialReadyMut = useMutation({ mutationFn: () => installationsApi.markMaterialReady(id), onSuccess: refresh, onError: onErr });
  const chkAddMut = useMutation({ mutationFn: () => installationsApi.addChecklistItem(id, chkTitle), onSuccess: () => { setChkTitle(''); refresh(); }, onError: onErr });
  const chkToggleMut = useMutation({ mutationFn: (itemId: number) => installationsApi.updateChecklistItem(id, itemId, { status: 'done' }), onSuccess: refresh, onError: onErr });
  const photoMut = useMutation({ mutationFn: () => installationsApi.addPhoto(id, { kind: photoKind, fileName: photoUrl.split('/').pop() || 'photo.jpg', fileUrl: photoUrl }), onSuccess: () => { setPhotoUrl(''); refresh(); }, onError: onErr });
  const commissionMut = useMutation({ mutationFn: () => installationsApi.commission(id, {}), onSuccess: refresh, onError: onErr });
  const signoffMut = useMutation({ mutationFn: () => installationsApi.signoff(id, { customerName }), onSuccess: refresh, onError: onErr });
  const handoverMut = useMutation({ mutationFn: () => installationsApi.requestHandover(id), onSuccess: refresh, onError: onErr });
  const approveMut = useMutation({ mutationFn: () => installationsApi.approveHandover(id, { certificateUrl: certUrl || undefined }), onSuccess: refresh, onError: onErr });
  const rejectMut = useMutation({ mutationFn: () => installationsApi.rejectHandover(id, rejectReason), onSuccess: refresh, onError: onErr });
  const closeMut = useMutation({ mutationFn: () => installationsApi.close(id), onSuccess: refresh, onError: onErr });
  const escalateMut = useMutation({ mutationFn: () => installationsApi.escalate(id, 'Manual escalation'), onSuccess: refresh, onError: onErr });

  if (!r) return null;
  const s = r.status;
  const can = (p: string) => hasPermission(p);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <span>{r.requestCode}</span>
          <StatusBadge status={r.status} />
          <Chip size="small" label={r.priority} />
          {r.slaBreached && <Chip size="small" color="error" label="SLA breached" />}
          {r.escalationLevel > 0 && <Chip size="small" color="warning" label={`Escalated L${r.escalationLevel}`} />}
          {r.materialReadyAt && <Chip size="small" color="info" label="Material ready" />}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Dealer</Typography><Typography>#{r.dealerId}</Typography></Grid>
          <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Technician</Typography><Typography>{r.technicianId ? `#${r.technicianId}` : '—'}</Typography></Grid>
          <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Scheduled</Typography><Typography>{r.scheduledDate ?? '—'}</Typography></Grid>
          <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Delay (days)</Typography><Typography>{r.delayDays}</Typography></Grid>
          {r.handoverCertificateUrl && <Grid item xs={12}><Link href={r.handoverCertificateUrl} target="_blank">Handover certificate</Link></Grid>}
        </Grid>

        <Divider textAlign="left" sx={{ mb: 1 }}>Checklist</Divider>
        {s === 'in_progress' && can('installation.execute') && (
          <Stack direction="row" spacing={1} mb={1}>
            <TextField size="small" label="Add checklist item" value={chkTitle} onChange={(e) => setChkTitle(e.target.value)} fullWidth />
            <Button size="small" disabled={!chkTitle} onClick={() => chkAddMut.mutate()}>Add</Button>
          </Stack>
        )}
        <Table size="small">
          <TableBody>
            {(r.checklist ?? []).map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.title}</TableCell>
                <TableCell><Chip size="small" label={c.status} color={c.status === 'done' ? 'success' : 'default'} /></TableCell>
                <TableCell align="right">{c.status !== 'done' && can('installation.execute') && <Button size="small" onClick={() => chkToggleMut.mutate(c.id)}>Done</Button>}</TableCell>
              </TableRow>
            ))}
            {(r.checklist ?? []).length === 0 && <TableRow><TableCell colSpan={3}>No checklist items.</TableCell></TableRow>}
          </TableBody>
        </Table>

        <Divider textAlign="left" sx={{ my: 1 }}>Photos (before / after / site)</Divider>
        {['in_progress', 'commissioned'].includes(s) && can('installation.execute') && (
          <Stack direction="row" spacing={1} mb={1} alignItems="center">
            <TextField select size="small" label="Kind" value={photoKind} onChange={(e) => setPhotoKind(e.target.value)} sx={{ minWidth: 120 }}>
              {['site', 'before', 'after', 'commission'].map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </TextField>
            <FileUploadField label="Photo" accept=".jpg,.jpeg,.png,.webp,.gif,.pdf" value={photoUrl || null} onChange={(url) => setPhotoUrl(url ?? '')} />
            <Button size="small" disabled={!photoUrl} onClick={() => photoMut.mutate()}>Upload</Button>
          </Stack>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {(r.photos ?? []).map((p) => <Chip key={p.id} size="small" label={`${p.kind}: ${p.fileName}`} component="a" href={p.fileUrl} target="_blank" clickable />)}
        </Stack>

        <Divider textAlign="left" sx={{ my: 1 }}>History</Divider>
        <Table size="small">
          <TableBody>
            {(r.history ?? []).map((h) => (
              <TableRow key={h.id}><TableCell>{h.action}</TableCell><TableCell>{h.fromStatus ?? '—'} → {h.toStatus}</TableCell><TableCell>{new Date(h.createdAt).toLocaleString()}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>

        <PdiCommentsThread scope="installation" entityId={id} list={installationsApi.listComments} add={installationsApi.addComment} canComment={can('installation.read')} />

        {/* Contextual inputs */}
        <Stack spacing={1} mt={2}>
          {s === 'requested' && can('installation.assign') && <TextField size="small" label="Technician ID" type="number" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)} />}
          {s === 'assigned' && can('installation.assign') && <TextField size="small" label="Scheduled date" type="date" InputLabelProps={{ shrink: true }} value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} />}
          {s === 'commissioned' && can('installation.signoff') && <TextField size="small" label="Customer name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />}
          {s === 'pending_handover' && can('installation.approve') && (
            <>
              <FileUploadField label="Certificate (optional)" value={certUrl || null} onChange={(url) => setCertUrl(url ?? '')} />
              <TextField size="small" label="Rejection reason (to reject)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {['requested', 'assigned', 'scheduled'].includes(s) && !r.materialReadyAt && can('installation.execute') && <Button color="info" variant="outlined" onClick={() => materialReadyMut.mutate()} disabled={materialReadyMut.isPending}>Mark Material Ready</Button>}
        {s === 'requested' && can('installation.assign') && <Button variant="contained" disabled={!technicianId || assignMut.isPending} onClick={() => assignMut.mutate()}>Assign</Button>}
        {s === 'assigned' && can('installation.assign') && <Button variant="contained" disabled={!scheduledDate || scheduleMut.isPending} onClick={() => scheduleMut.mutate()}>Schedule</Button>}
        {s === 'scheduled' && can('installation.execute') && <Button variant="contained" onClick={() => startMut.mutate()} disabled={startMut.isPending}>Start</Button>}
        {s === 'in_progress' && can('installation.commission') && <Button variant="contained" onClick={() => commissionMut.mutate()} disabled={commissionMut.isPending}>Commission</Button>}
        {s === 'commissioned' && can('installation.signoff') && <Button variant="contained" disabled={!customerName || signoffMut.isPending} onClick={() => signoffMut.mutate()}>Customer Sign-off</Button>}
        {s === 'signed_off' && can('installation.handover') && <Button variant="contained" onClick={() => handoverMut.mutate()} disabled={handoverMut.isPending}>Request Handover</Button>}
        {s === 'pending_handover' && can('installation.approve') && (
          <>
            <Button color="success" variant="contained" onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>Approve Handover</Button>
            <Button color="error" variant="outlined" disabled={!rejectReason || rejectMut.isPending} onClick={() => rejectMut.mutate()}>Reject</Button>
          </>
        )}
        {s === 'handed_over' && can('installation.handover') && <Button variant="contained" onClick={() => closeMut.mutate()} disabled={closeMut.isPending}>Close</Button>}
        {!['handed_over', 'closed', 'cancelled'].includes(s) && can('installation.escalate') && <Button color="warning" variant="outlined" onClick={() => escalateMut.mutate()} disabled={escalateMut.isPending}>Escalate</Button>}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
